import { useState } from 'react'

import { currencies, formatCurrency } from '@ankor-io/common/util/currencies'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import Tabs, { TabContent, TabItem } from '@/ui-components/Tabs'
import Conversions from '@/views/Wallet/Conversions'
import GlobalAccounts from '@/views/Wallet/GlobalAccounts'

// Just create some example types for global accounts and wallet transactions
type GlobalAccount = {
  id: string
  currency: string
  flag?: string
  country?: string
  accountName?: string
  iban?: string
  accountNumber?: string
  sortCode?: string
  transitNumber?: string
  status: 'active' | 'pending' | 'disabled'
}

type WalletTxn = {
  id: string
  date: string
  description: string
  amount: number
  currency?: string
  reference?: string
}

const WalletPage: React.FC = () => {
  const { activeConnectedAccountId } = useUserContext()
  const { connectedAccounts, isLoading } = useConnectedAccount()

  const [displayCurrency, setDisplayCurrency] = useState(currencies[0].code)
  const [activeTab, setActiveTab] = useState<'global-accounts' | 'conversions'>('global-accounts')

  const selectedConnectedAccount = connectedAccounts.find((a) => a.id === activeConnectedAccountId) || null

  // Simple mocks to get started
  const globalAccountsMock: GlobalAccount[] = [
    {
      id: 'ga-001',
      currency: 'EUR',
      flag: '🇪🇺',
      country: 'France',
      accountName: 'Vessel EUR Account',
      iban: 'FR14 2004 1010 0505 0001 3M02 606',
      status: 'active',
    },
    {
      id: 'ga-002',
      currency: 'USD',
      flag: '🇺🇸',
      country: 'United States',
      accountName: 'Vessel USD Account',
      accountNumber: '4000 1234 5678 9012',
      status: 'active',
    },
  ]

  const recentTransactionsMock: WalletTxn[] = [
    {
      id: 'TXN-W001',
      date: '2024-05-29',
      description: 'Charter deposit via EUR Global Account',
      amount: 35000,
      currency: 'EUR',
      reference: 'DEP-EUR-2024-001',
    },
    {
      id: 'TXN-W002',
      date: '2024-05-28',
      description: 'APA payment via USD Global Account',
      amount: 28500,
      currency: 'USD',
      reference: 'APA-USD-2024-002',
    },
  ]

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="space-y-6">
      <Heading level={3}>Digital Wallet</Heading>

      {/* Summary */}
      <div className="rounded-lg border p-4 sm:p-6 border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 justify-between">
          <div className="flex-1">
            <Heading level={6}>{selectedConnectedAccount?.name || 'Vessel'} - Total Balance</Heading>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Combined value across all currencies for this vessel
            </p>
            <div className="mt-3 text-3xl font-semibold tracking-wide">{formatCurrency(35000, displayCurrency)}</div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Converted from 4 currencies · Exchange rates updated in real-time
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Display Balance in
            </label>
            <Select
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency((e.target.value as typeof displayCurrency) || 'USD')}
            >
              {currencies.map((c) => (
                <option
                  key={c.code}
                  value={c.code}
                >
                  {c.code}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Top balances (hardcoded right now) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="rounded-lg border p-4 sm:p-6 border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-50">
            <span className="text-base">🇪🇺</span>
            EUR
          </div>
          <div className="mt-3 text-2xl font-semibold tracking-wide">{formatCurrency(1300, 'EUR')}</div>
        </div>
        <div className="rounded-lg border p-4 sm:p-6 border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-50">
            <span className="text-base">🇦🇺</span>
            AUD
          </div>
          <div className="mt-3 text-2xl font-semibold tracking-wide">{formatCurrency(1000, 'AUD')}</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs>
        <TabItem
          current={activeTab === 'global-accounts'}
          onClick={() => setActiveTab('global-accounts')}
        >
          Global Accounts
        </TabItem>
        <TabItem
          current={activeTab === 'conversions'}
          onClick={() => setActiveTab('conversions')}
        >
          Conversions
        </TabItem>

        <div className="flex-1" />

        {/* Primary actions aligned to the right when on Global Accounts */}
        {activeTab === 'global-accounts' && (
          <div className="ml-auto flex items-center gap-3">
            <Button
              color="primary"
              onClick={() => console.log('Pay clicked!')}
            >
              Pay
            </Button>
            <Button
              color="primary"
              to="/global-accounts/new"
            >
              Create Global Account
            </Button>
          </div>
        )}

        {activeTab === 'conversions' && (
          <div className="ml-auto flex items-center gap-3">
            <Button
              color="primary"
              to="/conversions/new"
            >
              New Conversion
            </Button>
          </div>
        )}
      </Tabs>

      <TabContent isActive={activeTab === 'global-accounts'}>
        <GlobalAccounts accounts={globalAccountsMock} />
      </TabContent>

      <TabContent isActive={activeTab === 'conversions'}>
        <Conversions transactions={recentTransactionsMock} />
      </TabContent>
    </div>
  )
}

export default WalletPage
